import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "../services/api";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref(null);
  const token = ref(localStorage.getItem("token") || null);
  const isLoading = ref(false);

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const isAdmin = computed(() => user.value?.role === "admin");
  const isHelper = computed(() => user.value?.role === "helper");

  // Actions
  const login = async (credentials) => {
    try {
      isLoading.value = true;
      console.log("Auth store: Making API call to /api/auth/login with:", credentials);
      console.log("Auth store: API base URL:", api.defaults.baseURL);
      console.log("Auth store: API headers:", api.defaults.headers);

      const response = await api.post("/api/auth/login", credentials);
      console.log("Auth store: API response:", response.data);

      if (response.data.success) {
        const { user: userData, token: authToken } = response.data.data;

        user.value = userData;
        token.value = authToken;

        localStorage.setItem("token", authToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

        return {
          success: true,
          mustChangePassword: userData.mustChangePassword || false
        };
      }
    } catch (error) {
      console.error("Auth store: Login error:", error);
      console.error("Auth store: Error response:", error.response);
      console.error("Auth store: Error status:", error.response?.status);
      console.error("Auth store: Error data:", error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || "Login failed"
      };
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (userData) => {
    try {
      isLoading.value = true;
      const response = await api.post("/api/auth/register", userData);

      if (response.data.success) {
        const { user: newUser, token: authToken } = response.data.data;

        user.value = newUser;
        token.value = authToken;

        localStorage.setItem("token", authToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Registration failed"
      };
    } finally {
      isLoading.value = false;
    }
  };

  const logout = () => {
    user.value = null;
    token.value = null;
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
  };

  const fetchProfile = async () => {
    try {
      if (!token.value) return;

      const response = await api.get("/api/auth/profile");
      if (response.data.success) {
        user.value = response.data.data.user;
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      logout();
    }
  };

  const updateProfile = async (profileData) => {
    try {
      isLoading.value = true;
      const response = await api.put("/api/auth/profile", profileData);

      if (response.data.success) {
        user.value = response.data.data.user;
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Profile update failed"
      };
    } finally {
      isLoading.value = false;
    }
  };

  const changePassword = async (passwordData) => {
    try {
      isLoading.value = true;
      const response = await api.put("/api/auth/profile", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data.success) {
        user.value = response.data.data.user;
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Password change failed"
      };
    } finally {
      isLoading.value = false;
    }
  };

  // Initialize auth state
  const initAuth = () => {
    if (token.value) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token.value}`;
      fetchProfile();
    }
  };

  return {
    // State
    user,
    token,
    isLoading,

    // Getters
    isAuthenticated,
    isAdmin,
    isHelper,

    // Actions
    login,
    register,
    logout,
    fetchProfile,
    updateProfile,
    changePassword,
    initAuth
  };
});
